import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import InspectionPhotos from '../../components/InspectionPhotos';
import './InspectionDetailPage.css';
import InspectionActions from '../../components/InspectionActions/InspectionActions';
import { useSpinner } from '../../context/SpinnerContext';
import { useAuthContext } from '../../context/AuthContext';
import inspectionService from '../../services/inspectionService';
import InspectionInfo from '../../components/InspectionInfo/InspectionInfo';
import { INSPECTION_AREAS, ROLES, INSPECTION_STATUS_VERDICT, INSPECTION_STATUS } from '../../utils/constants';
import { InspectionAreas } from '../../components/InspectionAreas';
import VisualInspectionSection from '../../components/VisualInspectionSection/VisualInspectionSection';
import Notification from '../../components/Notification';
import InspectionComments from '../../components/InspectionComments/InspectionComments';
import documentService from '../../services/documentService';
import photoService from '../../services/photoService';
import InspectionReport from '../../components/InspectionReport';
import InspectionHistoryTimeline from '../../components/InspectionHistoryTimeline';
import logger from '../../utils/logger';
import config from '../../config/environment';

const { INSPECTOR, CREATOR, CREATOR_SENIOR, ADMINISTRATOR } = ROLES;
const { APPROVED, REJECTED, IN_PROGRESS, PENDING_REVIEW } = INSPECTION_STATUS;
const { ANTECEDENTS, VERDICTS, VISUAL_INSPECTION } = INSPECTION_AREAS;
const { PENDING } = INSPECTION_STATUS_VERDICT;

const InspectionDetailPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [inspection, setInspection] = useState(null);
    const [notAllowed, setNotAllowed] = useState(false);
    const [notification, setNotification] = useState({ visible: false, message: '', type: 'info' });

    const [inspectionComments, setInspectionComments] = useState({ comments: [], attachments: [] });
    const [documentUrls, setDocumentUrls] = useState({});
    const [photoUrls, setPhotoUrls] = useState({});

    const { showSpinner, hideSpinner } = useSpinner();
    const { currentUser } = useAuthContext();
    const hasLoadedRef = useRef(false);

    const loadInspection = useCallback(async () => {
        try {
            showSpinner('Cargando inspección...');
            logger.info('Cargando inspección...', {
                id: id,
            }, 'InspectionDetailPage');
            const result = await inspectionService.getInspection(id);

            if ([INSPECTOR, CREATOR, CREATOR_SENIOR].includes(currentUser.role) && !currentUser.insuranceCompany.split(',').some(company => result.inspection.partnerData?.insuranceCompany.toLowerCase() === company.toLowerCase())) {
                setNotAllowed(true);
                return;
            }

            // Cargar URLs prefirmadas una sola vez y guardarlas en estado separado
            let urlsMap = {};
            try {
                const photosResult = await inspectionService.getInspectionPhotos(id);
                if (photosResult.photos && photosResult.photos.length > 0) {
                    // Crear mapeo de id -> url
                    photosResult.photos.forEach(photo => {
                        if (photo.id && photo.url) {
                            urlsMap[photo.id] = photo.url;
                        }
                    });
                    setPhotoUrls(urlsMap);

                    // Usar las fotos del servicio directamente (ya incluyen video360 con URLs)
                    result.inspection.photos = photosResult.photos;
                } else {
                    result.inspection.photos = [];
                    setPhotoUrls({});
                }
            } catch (photosError) {
                logger.error('Error loading photos:', photosError, null, 'InspectionDetailPage');
                result.inspection.photos = [];
                setPhotoUrls({});
            }

            setInspection(result.inspection);

            // Cargar comentarios si existen
            if (result.inspection.inspectionComments) {
                setInspectionComments(result.inspection.inspectionComments);
                // Cargar URLs de descarga para archivos adjuntos
                if (result.inspection.inspectionComments.attachments?.length > 0) {
                    await loadDocumentUrls(result.inspection.id, result.inspection.inspectionComments.attachments);
                }
            }
        } catch (error) {
            logger.error('Error loading inspection:', error, null, 'InspectionDetailPage');
            showNotification('Error al cargar la inspección. Inténtalo de nuevo.', 'error');
        } finally {
            hideSpinner()
        }
    }, [id, currentUser.role, currentUser.insuranceCompany, showSpinner, hideSpinner]);

    // Cargar inspección al montar el componente
    useEffect(() => {
        if (id && !hasLoadedRef.current) {
            hasLoadedRef.current = true;
            loadInspection();
        }
    }, [id, loadInspection]);

    const handleInformationCorrected = (updatedInspection) => {
        setInspection((prev) => prev ? ({
            ...prev,
            vehicleData: updatedInspection.vehicleData ?? prev.vehicleData,
            contactData: updatedInspection.contactData ?? prev.contactData,
            partnerData: updatedInspection.partnerData ?? prev.partnerData,
            accidentType: Object.prototype.hasOwnProperty.call(updatedInspection, 'accidentType')
                ? updatedInspection.accidentType
                : prev.accidentType,
            inspectionComments: updatedInspection.inspectionComments ?? prev.inspectionComments,
            updatedAt: updatedInspection.updatedAt ?? prev.updatedAt,
        }) : updatedInspection);
        if (updatedInspection.inspectionComments) {
            setInspectionComments(updatedInspection.inspectionComments);
        }
    };

    const showNotification = (message, type = 'info') => {
        setNotification({ visible: true, message, type });
        setTimeout(() => {
            setNotification({ visible: false, message: '', type: 'info' });
        }, 5000);
    };

    // Función helper para enriquecer fotos con URLs del estado separado
    const enrichPhotosWithUrls = useCallback((photos, urlsMap) => {
        if (!photos || !Array.isArray(photos)) return [];
        return photos.map(photo => ({
            ...photo,
            url: urlsMap[photo.id] || photo.url || '' // Usar URL del estado separado si existe
        }));
    }, []);

    const loadDocumentUrls = async (inspectionId, attachments) => {
        try {
            const urls = {};

            const downloadUrl = await documentService.getDownloadUrl(inspectionId, attachments);
            downloadUrl.forEach(file => {
                urls[file.key] = file.downloadUrl;
            });

            setDocumentUrls(urls);
        } catch (error) {
            logger.error('Error loading document URLs:', error, null, 'InspectionDetailPage');
        }
    };

    // Manejar envío de comentario
    const handleCommentSubmit = async (commentData, files) => {
        if (!canEditInspection()) {
            showNotification('No tienes permisos para agregar comentarios', 'error');
            return;
        }

        try {
            showSpinner('Guardando comentario...');
            logger.info('Guardando comentario...', {
                inspectionId: inspection?.id,
                commentData: commentData,
                filesCount: files?.length,
            }, 'InspectionDetailPage');
            let uploadedAttachments = [];
            // Subir archivos si hay
            if (files?.length > 0) {
                uploadedAttachments = await documentService.uploadDocuments(
                    files,
                    inspection?.id,
                    (progress) => {
                        logger.info(`Subiendo archivos: ${progress}%`, null, 'InspectionDetailPage');
                    }
                );
            }

            // Crear nuevo comentario
            const newComment = {
                id: Math.random().toString(36).substr(2, 6).toUpperCase(),
                author: {
                    name: currentUser.name,
                    role: currentUser.role
                },
                content: commentData.content,
                timestamp: new Date().toISOString(),
                attachments: uploadedAttachments
            };

            // Actualizar estado local
            const updatedComments = [...inspectionComments.comments, newComment];
            const updatedAttachments = [...inspectionComments.attachments, ...uploadedAttachments];

            const updatedInspectionComments = {
                comments: updatedComments,
                attachments: updatedAttachments
            };

            setInspectionComments(updatedInspectionComments);

            const updates = {
                inspectionComments: updatedInspectionComments
            };

            await updateInspectionInDynamo(updates);

            // Cargar URLs de descarga para los nuevos archivos
            if (uploadedAttachments.length > 0) {
                await loadDocumentUrls(inspection.id, uploadedAttachments);
            }

            showNotification('Comentario guardado exitosamente', 'success');
            logger.info('Comentario guardado exitosamente', {
                inspectionId: inspection?.id,
            }, 'InspectionDetailPage');

        } catch (error) {
            logger.error('Error saving comment:', error, null, 'InspectionDetailPage');
            showNotification('Error al guardar comentario', 'error');
        } finally {
            hideSpinner();
        }
    };

    // Descargar archivo
    const handleDownloadFile = async (attachment) => {
        try {
            let downloadUrl = documentUrls[attachment.id];

            if (!downloadUrl) {
                downloadUrl = await documentService.getDownloadUrl(attachment.id);
                setDocumentUrls(prev => ({
                    ...prev,
                    [attachment.id]: downloadUrl
                }));
            }

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = attachment.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            logger.error('Error downloading file:', error, null, 'InspectionDetailPage');
            showNotification('Error al descargar archivo', 'error');
        }
    };

    const updateInspectionInDynamo = async (updatedInspection) => {
        try {
            showSpinner('Actualizando inspección...');
            logger.info('Actualizando inspección...', {
                inspectionId: inspection?.id,
            }, 'InspectionDetailPage');
            const result = await inspectionService.updateInspection(id, updatedInspection);

            // Enriquecer las fotos que vienen de DynamoDB con las URLs del estado separado
            let enrichedPhotos = enrichPhotosWithUrls(result.inspection.photos || [], photoUrls);

            // Si hay video360 en DynamoDB, agregarlo al array de fotos para visualización
            if (result.inspection.video360) {
                const video360WithUrl = {
                    ...result.inspection.video360,
                    url: photoUrls[result.inspection.video360.id || 'video360'] || '',
                    isVideo: true
                };
                enrichedPhotos.push(video360WithUrl);
            }

            const enrichedInspection = {
                ...result.inspection,
                photos: enrichedPhotos
            };

            setInspection(enrichedInspection);
            logger.info('Inspección actualizada exitosamente', {
                inspectionId: inspection?.id,
            }, 'InspectionDetailPage');
        } catch (error) {
            logger.error('Error updating inspection:', error, null, 'InspectionDetailPage');
            throw error;
        } finally {
            hideSpinner();
        }
    };

    const handleSavePhotoChanges = async (updatedInspection) => {
        if (!canEditInspection()) {
            showNotification('No tienes permisos para editar esta inspección', 'error');
            return;
        }

        try {
            showSpinner('Guardando cambios de fotos...');
            logger.info('Guardando cambios de fotos...', {
                inspectionId: updatedInspection?.id,
            }, 'InspectionDetailPage');
            // Separar el video360 del array de fotos
            const video360 = updatedInspection.photos.find(photo => photo.isVideo || photo.id === 'video360');
            const photosWithoutVideo = updatedInspection.photos.filter(photo => !photo.isVideo && photo.id !== 'video360');

            const updates = {
                photos: photosWithoutVideo,
                photosInspection: updatedInspection.photosInspection,
                status: updatedInspection.status
            };

            // Si hay video360 con cambios, actualizarlo también
            if (video360) {
                // Excluir la propiedad 'url' (URL prefirmada temporal) antes de guardar
                const { url, ...video360WithoutUrl } = video360;
                updates.video360 = video360WithoutUrl;
            }

            await updateInspectionInDynamo(updates);

            // Notificar fotos rechazadas
            const rejectedPhotos = updatedInspection.photos.filter(photo => photo.status === REJECTED).map(photo => ({ photoName: photo.label, rejectionReason: photo.rejectionReason }));
            if (rejectedPhotos.length > 0) {
                await inspectionService.notifyPhotosRejected(updatedInspection.id, rejectedPhotos);
            }

            logger.info('Cambios de fotos guardados exitosamente', {
                inspectionId: updatedInspection?.id,
            }, 'InspectionDetailPage');
            showNotification('Cambios de fotos guardados exitosamente', 'success');
        } catch (error) {
            logger.error('Error saving photo changes:', error, null, 'InspectionDetailPage');
            showNotification('Error al guardar los cambios de fotos. Inténtalo de nuevo.', 'error');
        } finally {
            hideSpinner();
        }
    };

    const isAssignedToUser = (area) => {
        return inspection[area]?.assignedTo?.email.toLowerCase() === currentUser.email.toLowerCase();
    };

    const assignPhotosSectionToUser = async () => {
        if (!inspection) return;
        logger.info('Asignando sección de fotos al usuario...', {
            inspectionId: inspection?.id,
            currentUserEmail: currentUser?.email,
        }, 'InspectionDetailPage');
        const currentTime = new Date().toISOString();
        const currentUserInfo = {
            name: currentUser?.name,
            email: currentUser?.email
        };

        const updates = {
            photosInspection: {
                ...inspection.photosInspection,
                assignedTo: currentUserInfo,
                assignedAt: currentTime,
                status: IN_PROGRESS
            }
        };

        await updateInspectionInDynamo(updates);
    };

    const handleVerdictChange = (field, value) => {
        if (!inspection || !isAssignedToUser(VERDICTS)) return;

        const updatedInspection = {
            ...inspection,
            verdict: {
                ...inspection.verdict,
                verdictFields: {
                    ...inspection.verdict?.verdictFields,
                    [field]: value
                }
            }
        };

        setInspection(updatedInspection);
    };

    const handleVerdictCommentsChange = (comments) => {
        if (!inspection || !isAssignedToUser(VERDICTS)) return;

        const updatedInspection = {
            ...inspection,
            verdict: {
                ...inspection.verdict,
                comments: comments
            }
        };

        setInspection(updatedInspection);
    };

    const handleAntecedentsChange = (hasAntecedents) => {
        if (!inspection || !isAssignedToUser(ANTECEDENTS)) return;

        const updatedInspection = {
            ...inspection,
            antecedents: {
                ...inspection.antecedents,
                hasAntecedents: hasAntecedents
            }
        };

        setInspection(updatedInspection);
    };

    const handleAntecedentsCommentsChange = (comments) => {
        if (!inspection || !isAssignedToUser(ANTECEDENTS)) return;

        const updatedInspection = {
            ...inspection,
            antecedents: {
                ...inspection.antecedents,
                antecedentsComments: comments
            }
        };

        setInspection(updatedInspection);
    };

    const canManageArea = (area) => {

        if (!currentUser || !inspection) return false;

        // Inspección visual solo aplica a inspecciones digitales (no dictamen)
        if (area === VISUAL_INSPECTION && inspection.id?.startsWith('DIC-')) return false;

        // Administradores siempre pueden gestionar
        if (currentUser.role === ADMINISTRATOR) return true;

        // Inspectores solo si tienen permisos y el área no está asignada
        if (currentUser.role === INSPECTOR) {
            const areaData = area === ANTECEDENTS ? inspection.antecedents : area === VERDICTS ? inspection.verdict : inspection.visualInspection;
            const hasPermission = area === ANTECEDENTS
                ? currentUser.inspectionAreas?.includes(ANTECEDENTS)
                : area === VERDICTS
                    ? currentUser.inspectionAreas?.includes(VERDICTS)
                    : currentUser.inspectionAreas?.includes(VISUAL_INSPECTION);
            const isActivelyAssigned = areaData?.assignedTo && areaData?.status === IN_PROGRESS;
            const notAssigned = !isActivelyAssigned;

            return hasPermission && notAssigned;
        }

        return false;
    };

    const canEditInspection = () => {
        if (!currentUser || !inspection) return false;

        // Administradores siempre pueden editar
        if (currentUser.role === ADMINISTRATOR) return true;

        // Inspectores siempre pueden editar
        if (currentUser.role === INSPECTOR) return true;

        // Creador Senior puede editar
        if (currentUser.role === CREATOR_SENIOR) return true;

        // Creador básico NO puede editar
        if (currentUser.role === CREATOR) return false;

        return false;
    };

    const assignAreaToUser = async (area, userInfo) => {
        if (!inspection) return false;

        const fresh = (await inspectionService.getInspection(inspection.id)).inspection;
        const areaStatus = fresh?.[area]?.status;
        const inspectionClosed = [APPROVED, REJECTED].includes(fresh?.status);
        if (areaStatus !== PENDING || inspectionClosed) {
            setInspection(fresh);
            showNotification('El área ya fue tomada o la inspección ya finalizó. Se actualizó la pantalla.', 'error');
            return false;
        }

        const currentTime = new Date().toISOString();
        const updates = {
            [area]: {
                ...fresh[area],
                assignedTo: userInfo,
                assignedAt: currentTime,
                status: IN_PROGRESS
            }
        };

        try {
            await updateInspectionInDynamo(updates);
            logger.info('Área asignada exitosamente', {
                inspectionId: inspection?.id,
                area: area,
                userInfo: userInfo,
            }, 'InspectionDetailPage');
            return true;
        } catch (error) {
            const reloaded = (await inspectionService.getInspection(inspection.id)).inspection;
            setInspection(reloaded);
            showNotification(error.message || 'No se pudo iniciar el área', 'error');
            return false;
        }
    };

    const mergeOfficialRecordsIntoInspection = (officialRecords) => {
        setInspection(prev => ({
            ...prev,
            antecedents: {
                ...prev.antecedents,
                officialRecords,
            }
        }));
    };

    const consultOfficialRecordsAndMerge = async (payload) => {
        try {
            showSpinner('Consultando antecedentes...');
            const result = await inspectionService.consultOfficialRecords(id, payload);
            mergeOfficialRecordsIntoInspection(result.officialRecords);
        } catch (error) {
            logger.error('Error consultando antecedentes oficiales:', error, null, 'InspectionDetailPage');
            mergeOfficialRecordsIntoInspection(error.officialRecords || {
                error: {
                    message: error.message || 'Error al consultar los antecedentes oficiales',
                    at: new Date().toISOString(),
                    consultBy: payload?.consultBy,
                }
            });
        } finally {
            hideSpinner();
        }
    };

    const handleStartArea = async (area, payload) => {
        if (!canManageArea(area)) return;

        const userInfo = {
            name: currentUser?.name,
            email: currentUser?.email
        };

        const started = await assignAreaToUser(area, userInfo);
        if (!started) return;
        if (area === ANTECEDENTS && config.ENABLE_OFFICIAL_RECORDS) {
            await consultOfficialRecordsAndMerge(payload);
        }
    };

    const handleRetryPlacApi = async (payload) => {
        await consultOfficialRecordsAndMerge(payload);
    };

    const handleCancelAntecedents = () => {
        // Limpiar campos de antecedentes
        const updatedInspection = {
            ...inspection,
            antecedents: {
                ...inspection.antecedents,
                hasAntecedents: undefined,
                antecedentsComments: ''
            }
        };
        setInspection(updatedInspection);
    };

    const handleCancelVerdict = () => {
        // Limpiar campos de veredicto
        const updatedInspection = {
            ...inspection,
            verdict: {
                ...inspection.verdict,
                verdictFields: {},
                comments: ''
            }
        };
        setInspection(updatedInspection);
    };

    const handleApproveInspection = async (areaType) => {
        await approveRejectArea(areaType === 'inspection' ? VERDICTS : ANTECEDENTS, APPROVED);
    };

    const handleRejectInspection = async (areaType) => {
        await approveRejectArea(areaType === 'inspection' ? VERDICTS : ANTECEDENTS, REJECTED);
    };

    const approveRejectArea = async (area, action) => {
        logger.info('Aprobando/rechazando área...', {
            inspectionId: inspection?.id,
            area: area,
            action: action,
            currentUserEmail: currentUser?.email,
        }, 'InspectionDetailPage');
        const currentTime = new Date().toISOString();
        const userInfo = {
            name: currentUser?.name,
            email: currentUser?.email
        };
        const response = await inspectionService.getInspection(inspection.id);
        const currentInspection = response.inspection;

        const updates = {};

        if (area === ANTECEDENTS) {
            updates.antecedents = {
                ...currentInspection.antecedents,
                ...inspection.antecedents,
                status: action === APPROVED ? APPROVED : REJECTED,
                finishedBy: userInfo.name,
                finishedAt: currentTime
            };
        } else if (area === VERDICTS) {
            updates.verdict = {
                ...currentInspection.verdict,
                ...inspection.verdict,
                status: action === APPROVED ? APPROVED : REJECTED,
                finishedBy: userInfo.name,
                finishedAt: currentTime
            };
        } else if (area === VISUAL_INSPECTION) {
            updates.visualInspection = {
                ...currentInspection.visualInspection,
                ...inspection.visualInspection,
                status: action === APPROVED ? APPROVED : REJECTED,
                finishedBy: userInfo.name,
                finishedAt: currentTime
            };
        }

        const updatedStatus = validateInspectionAreaCompleted({
            ...currentInspection,
            ...updates
        });
        updates.status = updatedStatus;

        if ([REJECTED, APPROVED].includes(updatedStatus)) {
            const updatedPhotos = (currentInspection.photos || []).map(photo => {
                const photoStatus = photo.status || PENDING;
                if (!photoStatus || photoStatus === PENDING || photoStatus === 'pendiente') {
                    return {
                        ...photo,
                        status: APPROVED,
                        finishedBy: userInfo.name,
                        finishedAt: currentTime
                    };
                }
                return photo;
            });
            updates.photos = updatedPhotos;

            const updatedVideo360 = currentInspection.video360?.status === PENDING ? {
                ...currentInspection.video360,
                status: APPROVED,
                finishedBy: userInfo.name,
                finishedAt: currentTime
            } : currentInspection.video360;
            updates.video360 = updatedVideo360;
        }

        await updateInspectionInDynamo(updates);

        if ([REJECTED, APPROVED].includes(updatedStatus)) {
            await inspectionService.finalizeInspection(inspection.id);
        }
    };

    const validateInspectionAreaCompleted = (currentInspection) => {
        const antecedentsState = currentInspection.antecedents?.status;
        const verdictState = currentInspection.verdict?.status;
        const isDigital = currentInspection.id && !currentInspection.id.startsWith('DIC-');
        const visualState = isDigital ? currentInspection.visualInspection?.status : null;

        if (isDigital) {
            // Inspección digital: tres áreas (antecedents, verdict, visualInspection). Solo finalizar cuando las tres estén completadas.
            const allCompleted = [APPROVED, REJECTED].includes(antecedentsState) &&
                [APPROVED, REJECTED].includes(verdictState) &&
                [APPROVED, REJECTED].includes(visualState);
            if (allCompleted && antecedentsState === APPROVED && verdictState === APPROVED && visualState === APPROVED) {
                return APPROVED;
            }
            if (allCompleted && (antecedentsState === REJECTED || verdictState === REJECTED || visualState === REJECTED)) {
                return REJECTED;
            }
        } else {
            const antecedentsDone = [APPROVED, REJECTED].includes(antecedentsState);
            const verdictDone = [APPROVED, REJECTED].includes(verdictState);
            if (antecedentsDone && verdictDone) {
                return antecedentsState === APPROVED && verdictState === APPROVED ? APPROVED : REJECTED;
            }
        }

        return currentInspection.status;
    };

    const handleRevertResult = async (area) => {
        try {
            showSpinner('Revertir resultado...');
            logger.info('Revertir resultado...', {
                inspectionId: inspection?.id,
                area: area,
                currentUserEmail: currentUser?.email,
            }, 'InspectionDetailPage');
            const inspectionArea = area === ANTECEDENTS ? 'antecedents' : area === VERDICTS ? 'verdict' : 'visualInspection';

            const revertedAreaState = area === VISUAL_INSPECTION
                ? {
                    status: PENDING,
                    latoneria: {},
                    pintura: {},
                    vidrios: {},
                    comments: ''
                }
                : area === ANTECEDENTS
                    ? { status: PENDING, antecedentsComments: '' }
                    : { status: PENDING, verdictFields: {}, comments: '' };

            const updates = {
                [inspectionArea]: revertedAreaState,
                status: PENDING_REVIEW,
                finishedAt: null
            };

            await updateInspectionInDynamo(updates);

        } catch (error) {
            logger.error('Error reverting result:', error, null, 'InspectionDetailPage');
            showNotification('Error al revertir el resultado. Inténtalo de nuevo.', 'error');
        } finally {
            hideSpinner();
        }
    };

    const enableApproveRejectButtons = (area) => {
        const areaData = area === ANTECEDENTS ? inspection.antecedents : area === VERDICTS ? inspection.verdict : inspection.visualInspection;
        if (currentUser.email.toLowerCase() === areaData?.assignedTo?.email.toLowerCase() && areaData?.status === IN_PROGRESS) {
            return true;
        }
        return false;
    };

    const enableStartButton = (area) => {
        const { antecedents, verdict, visualInspection } = inspection;
        const areaData = area === ANTECEDENTS ? antecedents : area === VERDICTS ? verdict : visualInspection;
        const isPending = areaData?.status === PENDING || (area === VISUAL_INSPECTION && !areaData);

        if ((currentUser.role === 'Administrador' || (currentUser.role === 'Inspector' && currentUser.inspectionAreas?.includes(area))) && isPending) {
            return true;
        }
        return false;
    };

    const enableRevertResultButton = (area) => {
        const inspectionArea = area === ANTECEDENTS ? inspection.antecedents : area === VERDICTS ? inspection.verdict : inspection.visualInspection;
        if (
            currentUser.role === ADMINISTRATOR &&
            inspectionArea &&
            [APPROVED, REJECTED, IN_PROGRESS].includes(inspectionArea.status)
        ) {
            return true;
        }
        return false;
    };

    const isVerdictAreaComplete = () => {
        if (!inspection?.verdict?.verdictFields) return false;

        const requiredFields = ['chassis', 'engine', 'serialPlate'];

        // Verificar que todos los campos obligatorios estén completados
        const requiredComplete = requiredFields.every(field =>
            inspection.verdict.verdictFields[field] &&
            inspection.verdict.verdictFields[field].trim() !== ''
        );

        return requiredComplete;
    };

    const handleDownloadPhotosZip = async (inspectionId) => {
        if (!inspection || !inspection.photos || inspection.photos.length === 0) {
            showNotification('No hay fotos para descargar', 'error');
            return;
        }

        try {
            showSpinner('Generando archivo ZIP de fotos...');
            logger.info('Generando archivo ZIP de fotos...', {
                inspectionId: inspectionId,
            }, 'InspectionDetailPage');
            const response = await photoService.downloadPhotosZip(inspectionId);

            if (response.success && response.downloadUrl) {
                const link = document.createElement('a');
                link.href = response.downloadUrl;
                link.download = response.zipFileName || `${inspectionId}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                showNotification('Archivo ZIP descargado exitosamente', 'success');
                logger.info('ZIP descargado exitosamente:', {
                    inspectionId: inspectionId,
                    zipFileName: response.zipFileName,
                }, 'InspectionDetailPage');
            } else {
                throw new Error(response.error || 'Error generando el archivo ZIP');
            }

        } catch (error) {
            logger.error('Error downloading photos ZIP:', error, null, 'InspectionDetailPage');
            showNotification(error.message || 'Error al descargar las fotos', 'error');
        } finally {
            hideSpinner();
        }
    };

    const handleDownloadPhoto = async (photo) => {
        if (!photo || !photo.id) {
            showNotification('No se pudo obtener la información de la foto', 'error');
            return;
        }

        try {
            showSpinner('Generando descarga de foto...');
            logger.info('Generando descarga de foto...', {
                inspectionId: inspection.id,
                photoId: photo.id,
            }, 'InspectionDetailPage');
            // Llamar al servicio para generar URL de descarga
            const response = await photoService.downloadPhoto(inspection.id, photo.s3Key);

            if (response.success && response.downloadUrl) {
                // Crear un enlace temporal para descargar el archivo
                const link = document.createElement('a');
                link.href = response.downloadUrl;
                link.download = response.fileName || photo.name || 'foto.jpg';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                showNotification('Foto descargada exitosamente', 'success');
                logger.info('Foto descargada exitosamente:', {
                    inspectionId: inspection.id,
                    photoId: photo.id,
                    fileName: response.fileName,
                }, 'InspectionDetailPage');
            } else {
                throw new Error(response.error || 'Error generando la descarga de la foto');
            }

        } catch (error) {
            logger.error('Error downloading photo:', error, null, 'InspectionDetailPage');
            showNotification(error.message || 'Error al descargar la foto', 'error');
        } finally {
            hideSpinner();
        }
    };

    const handleForcedRejectInspection = async (commentData) => {
        if (!inspection) return;
        showSpinner('Rechazando inspección...');
        logger.info('Rechazando inspección...', {
            inspectionId: inspection?.id,
            currentUserEmail: currentUser?.email,
        }, 'InspectionDetailPage');
        try {
            const newComment = {
                id: Math.random().toString(36).substr(2, 6).toUpperCase(),
                author: {
                    name: currentUser.name,
                    role: currentUser.role
                },
                content: commentData.content,
                timestamp: new Date().toISOString(),
                attachments: []
            };

            const updatedComments = [...inspectionComments.comments, newComment];
            const updatedAttachments = [...inspectionComments.attachments, ...[]];

            const updatedInspectionComments = {
                comments: updatedComments,
                attachments: updatedAttachments
            };

            setInspectionComments(updatedInspectionComments);

            const updates = {
                inspectionComments: updatedInspectionComments,
                status: REJECTED
            };

            await updateInspectionInDynamo(updates);

        } catch (error) {
            logger.error('Error rejecting inspection:', error, null, 'InspectionDetailPage');
            showNotification('Error al rechazar la inspección. Inténtalo de nuevo.', 'error');
            throw error;
        } finally {
            hideSpinner();
        }
    };

    const enableInspectionReportSection = () => {
        // Si es creador básico, nunca mostrar el informe
        if (currentUser.role === CREATOR) return false;

        // Para otros roles, validar estado de inspección
        return [APPROVED, REJECTED].includes(inspection.status) &&
            (inspection.antecedents?.status !== PENDING || inspection.verdict?.status !== PENDING);
    };

    if (notAllowed) {

        return (
            <div className="inspection-detail-page">
                <Header />
                <main className="inspection-content-wrapper">
                    <div className="error-container">
                        <p>No tienes permisos para ver esta inspección</p>
                        <button className="btn btn-primary" onClick={() => navigate('/')}>Volver al dashboard</button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // Si no hay inspección
    if (!inspection) {
        return (
            <div className="inspection-detail-page">
                <Header />
                <main className="inspection-content-wrapper">
                    <div className="error-container">
                        <p>No se encontró la inspección</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="inspection-detail-page">
            <Header />

            <main className="inspection-content-wrapper">
                {/* Page Title */}
                <div className="page-title-section">
                    <div className="title-content">
                        <div className="title-row">
                            <svg className="car-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"></path>
                                <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"></path>
                                <path d="M5 17h-2v-6l2-5h9l4 5h1a2 2 0 0 1 2 2v4h-2m-4 0h-6m-6 -6h15m-6 0v-5"></path>
                            </svg>
                            <h1>Inspección Vehicular</h1>
                        </div>
                        <p className="page-subtitle">Revisa y documenta el estado del vehículo para completar el proceso de inspección</p>
                    </div>
                </div>

                <div className="inspection-content">
                    {/* Inspection Info Section */}
                    <div className="section-card">
                        <h2 className="section-title">Información de la Inspección</h2>
                        <InspectionInfo
                            inspectionId={id}
                            inspectionData={inspection}
                            onInspectionUpdate={handleInformationCorrected}
                        />
                    </div>

                    {inspection.id && !inspection.id.startsWith('DIC-') && (
                        <div className="section-card">
                            <h2 className="section-title">Historial de la Inspección</h2>
                            <InspectionHistoryTimeline statusHistory={inspection.statusHistory || []} />
                        </div>
                    )}

                    {/* Inspection Report Section */}
                    {enableInspectionReportSection() && (
                        <div className="section-card">
                            <InspectionReport
                                inspectionId={inspection.id}
                                inspectionStatus={inspection.status}
                            />
                        </div>
                    )}

                    {/* Photos and Documents Section */}
                    <InspectionPhotos
                        inspection={inspection}
                        photoUrls={photoUrls}
                        assignPhotosSectionToUser={assignPhotosSectionToUser}
                        isAssignedToUser={() => isAssignedToUser('photosInspection')}
                        enableRequestNewPhotos={() => false}
                        onSavePhotoChanges={canEditInspection() ? handleSavePhotoChanges : null}
                        onDownloadPhotosZip={handleDownloadPhotosZip}
                        onDownloadPhoto={handleDownloadPhoto}
                    />

                    {/* Visual Inspection Section (solo inspecciones digitales, no dictamen) */}
                    {inspection.id && !inspection.id.startsWith('DIC-') && (
                        <div className="section-card">
                            <VisualInspectionSection
                                visualInspection={inspection.visualInspection || {}}
                                onStart={() => handleStartArea(VISUAL_INSPECTION)}
                                onApprove={() => approveRejectArea(VISUAL_INSPECTION, APPROVED)}
                                onReject={() => approveRejectArea(VISUAL_INSPECTION, REJECTED)}
                                onRevert={() => handleRevertResult(VISUAL_INSPECTION)}
                                onChange={(next) => {
                                    setInspection(prev => ({ ...prev, visualInspection: next }));
                                }}
                                enableStartButton={enableStartButton(VISUAL_INSPECTION)}
                                enableApproveRejectButtons={enableApproveRejectButtons(VISUAL_INSPECTION)}
                                enableRevertResultButton={enableRevertResultButton(VISUAL_INSPECTION)}
                                currentUser={currentUser}
                            />
                        </div>
                    )}

                    {/* Inspection Areas Section */}
                    <InspectionAreas
                        inspection={inspection}
                        currentUser={currentUser}
                        onVerdictChange={handleVerdictChange}
                        onVerdictCommentsChange={handleVerdictCommentsChange}
                        onAntecedentsChange={handleAntecedentsChange}
                        onAntecedentsCommentsChange={handleAntecedentsCommentsChange}
                        onStartArea={handleStartArea}
                        onCancelAntecedents={handleCancelAntecedents}
                        onCancelVerdict={handleCancelVerdict}
                        onApproveInspection={handleApproveInspection}
                        onRejectInspection={handleRejectInspection}
                        onRevertResult={handleRevertResult}
                        enableApproveRejectButtons={enableApproveRejectButtons}
                        enableStartButton={enableStartButton}
                        enableRevertResultButton={enableRevertResultButton}
                        isVerdictAreaComplete={isVerdictAreaComplete}
                        rejectedPhotosCount={inspection.photos?.filter(p => p.status === REJECTED || p.status === 'rechazado').length || 0}
                        onRetryPlacApi={handleRetryPlacApi}
                    />

                    <InspectionComments
                        isDisabled={[APPROVED, REJECTED].includes(inspection.status) || !canEditInspection()}
                        inspectionId={inspection.id}
                        inspectionComments={inspectionComments}
                        documentUrls={documentUrls}
                        onCommentSubmit={handleCommentSubmit}
                        onDownloadFile={handleDownloadFile}
                    />
                </div>

                {/* Footer Actions */}
                <InspectionActions
                    inspectionId={inspection.id}
                    onRejectInspection={handleForcedRejectInspection}
                    inspectionStatus={inspection.status}
                />
            </main>

            <Footer />

            {/* Notification Component */}
            <Notification
                visible={notification.visible}
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification({ visible: false, message: '', type: 'info' })}
            />
        </div>
    );
};

export default InspectionDetailPage; 
